import { CalendarItemDeleteConfirmation } from '$tablet-app/src/app/components/CalendarItemDeleteConfirmation/CalendarItemDeleteConfirmation';
import CalendarItemRecurrenceDeleteConfirmation from '$tablet-app/src/app/components/CalendarItemRecurrenceDeleteConfirmation';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { Panel } from '$tablet-app/src/app/components/Panel';
import { setChecklistItem } from '$tablet-app/src/app/models';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator';
import { Button } from '@akin-home/buttons/native';
import { isRecurringItem } from '@akin-home/calendar';
import {
  CalendarItemUnion,
  api_query_keys,
  useDeleteCalendarItem,
  useGetCalendarItem,
  useUpdateCalendarItem,
} from '@akin-home/calendar/combined';
import { CheckListItem } from '@akin-home/checklists';
import { Recipe } from '@akin-home/recipes';
import { useListAllRecipeModels } from '@akin-home/recipes/react';
import { getOriginIDFromRecurrenceID } from '@akin-home/recurrence';
import { Text } from '@akin-home/text/native';
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from '@akin-home/toast/native';
import { useBool } from '@akin-home/utils/react';
import { useEffect } from 'react';
import { StyleSheet } from 'react-native';
import { ScrollView } from 'react-native-web-hover';
import { useQueryClient } from 'react-query';
import styled from 'styled-components/native';
import { CalendarItemDetailsBody } from '../CalendarItemDetailsBody';

export function CalendarItemDetailsScreen(
  props: RootStackScreenProps<'CalendarItemDetails'>
) {
  const {
    route: {
      params: { id, type },
    },
    navigation: { goBack, replace },
  } = props;

  const query_client = useQueryClient();

  const { item, isLoading } = useGetCalendarItem({ id, type });
  const recipe_ids =
    item && 'recipes' in item ? item.recipes.map((r) => r.id) : [];
  const { recipes } = useListAllRecipeModels({
    params: {
      filter: {
        or: recipe_ids.map((id) => ({ id: { eq: id } })),
      },
    },
    options: {
      enabled: item?.type === 'Meal' && !!item.recipes?.length,
    },
  });

  const [delete_visible, showDelete, hideDelete] = useBool();
  const deleteCalendarItem = useDeleteCalendarItem();
  const updateCalendarItem = useUpdateCalendarItem();

  const onPressEdit = () => replace('EditCalendarItem', { id, type });
  const onPressRecipe = (recipe: Recipe) => {
    replace('RecipeDetails', { recipe_id: recipe.id });
  };

  const onUpdateChecklistItem = (
    item: CalendarItemUnion,
    checklistItem: CheckListItem
  ) => {
    updateCalendarItem(setChecklistItem(item, checklistItem));
  };

  const onConfirmDelete = async () => {
    showInfoToast({
      text1: `Deleting ${type}`,
    });
    goBack();

    await deleteCalendarItem({ id, type });

    showSuccessToast({
      text1: `Deleted ${type}`,
    });
  };

  const onConfirmDeleteAll = async () => {
    showInfoToast({
      text1: `Deleting recurring ${type}`,
    });
    goBack();

    const origin_id = getOriginIDFromRecurrenceID(id);

    if (!origin_id) return;

    await deleteCalendarItem({
      id: origin_id,
      type,
    });

    query_client.invalidateQueries(api_query_keys[type]);

    showSuccessToast({
      text1: `Deleted recurring ${type}`,
    });
  };

  useEffect(() => {
    if (!isLoading && !item) {
      showErrorToast({
        text1: 'Calendar item not found',
      });
      goBack();
    }
  }, [item, isLoading, goBack]);

  return (
    <Screen>
      <Header>
        <Button
          variant="secondary"
          size="large"
          icon="arrow-left"
          onPress={goBack}
        />
        <Text variant="h2" weight="semibold">
          {type}
        </Text>
      </Header>

      <ScrollView
        style={styles.body}
        contentContainerStyle={styles.scrollViewContentContainerStyle}
      >
        <Panel style={styles.panel}>
          {!item ? (
            <FullScreenLoader />
          ) : (
            <>
              <CalendarItemDetailsBody
                item={item}
                recipes={recipes}
                onPressEdit={onPressEdit}
                onPressDelete={showDelete}
                onPressRecipe={onPressRecipe}
                onChecklistPress={onUpdateChecklistItem}
              />

              {isRecurringItem(item) ? (
                <CalendarItemRecurrenceDeleteConfirmation
                  item={item}
                  visible={delete_visible}
                  onConfirmDeleteAll={onConfirmDeleteAll}
                  onConfirmDeleteSingle={onConfirmDelete}
                  onCancel={hideDelete}
                />
              ) : (
                <CalendarItemDeleteConfirmation
                  item={item}
                  onConfirm={onConfirmDelete}
                  visible={delete_visible}
                  onCancel={hideDelete}
                />
              )}
            </>
          )}
        </Panel>
      </ScrollView>
    </Screen>
  );
}

const Screen = styled.View`
  flex: 1;
`;

const Header = styled.View`
  flex-direction: row;
  align-items: center;
  padding: 24px 20px;
  gap: 20px;
`;

const styles = StyleSheet.create({
  body: {
    height: '100%',
    contentContainerStyle: { flexGrow: 1 },
  },
  panel: {
    height: '100%',
  },
  scrollViewContentContainerStyle: {
    padding: 20,
    paddingTop: 0,
    flexGrow: 1,
  },
});
